import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import type {
  CreateNotificationDTO,
  MaintenanceNotification,
  NotificationPage,
  NotificationSyncStatusEnum,
  RejectNotificationDTO,
} from '@mymaintenance/shared';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { toHttpParams } from '../utils/api.utils';

export type NotificationListFilters = {
  syncStatus?: NotificationSyncStatusEnum;
  mine?: boolean;
  functionalLocation?: string;
  equipment?: string;
  active?: boolean;
  limit?: number;
  cursor?: string;
};

const MAX_PAGE_SIZE = 100;

@Injectable({ providedIn: 'root' })
export class NotificationApi {
  readonly #http = inject(HttpClient);
  readonly #url = `${environment.api}/notifications`;

  create(notification: CreateNotificationDTO): Promise<MaintenanceNotification> {
    return firstValueFrom(this.#http.post<MaintenanceNotification>(this.#url, notification));
  }

  get(id: string): Promise<MaintenanceNotification> {
    return firstValueFrom(this.#http.get<MaintenanceNotification>(`${this.#url}/${id}`));
  }

  list(filters: NotificationListFilters): Promise<NotificationPage> {
    return firstValueFrom(
      this.#http.get<NotificationPage>(this.#url, { params: toHttpParams(filters) }),
    );
  }

  async listAll(filters: NotificationListFilters): Promise<MaintenanceNotification[]> {
    const items: MaintenanceNotification[] = [];
    let cursor: string | undefined;
    do {
      const page = await this.list({ ...filters, limit: MAX_PAGE_SIZE, cursor });
      items.push(...page.items);
      cursor = page.cursor;
    } while (cursor);
    return items;
  }

  /** With a `correction`, the notification is replaced by it before being approved. */
  approve(id: string, correction?: CreateNotificationDTO): Promise<MaintenanceNotification> {
    return firstValueFrom(
      this.#http.post<MaintenanceNotification>(`${this.#url}/${id}/approve`, correction),
    );
  }

  reject(id: string, rejection: RejectNotificationDTO): Promise<MaintenanceNotification> {
    return firstValueFrom(
      this.#http.post<MaintenanceNotification>(`${this.#url}/${id}/reject`, rejection),
    );
  }
}
